import React, { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserPlus, Loader2 } from "lucide-react";

const BLANK = { name: "", email: "", password: "", role: "client" };

export function CreateUserDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/admin/users", form);
      toast.success(`${data.user.role === "admin" ? "Admin" : "Client"} account created for ${data.user.email}`);
      setForm(BLANK);
      setOpen(false);
      onCreated?.(data.user);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="bg-brand-green hover:bg-brand-greenHover text-white" data-testid="admin-create-user-open">
          <UserPlus className="w-4 h-4 mr-1.5" /> Create user
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md bg-white" data-testid="create-user-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-brand-green">Create a new user</DialogTitle>
          <DialogDescription className="text-sm text-brand-muted">Add a client or another administrator. They can sign in with the details below.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <Label className="text-sm font-semibold text-brand-ink mb-1.5 inline-block">Full name</Label>
            <Input required value={form.name} onChange={(e) => set("name", e.target.value)}
              className="bg-white border-brand-line focus-visible:ring-brand-green" data-testid="create-user-name" />
          </div>
          <div>
            <Label className="text-sm font-semibold text-brand-ink mb-1.5 inline-block">Email</Label>
            <Input type="email" required value={form.email} onChange={(e) => set("email", e.target.value)}
              className="bg-white border-brand-line focus-visible:ring-brand-green" data-testid="create-user-email" />
          </div>
          <div>
            <Label className="text-sm font-semibold text-brand-ink mb-1.5 inline-block">Temporary password</Label>
            <Input type="text" required value={form.password} onChange={(e) => set("password", e.target.value)}
              className="bg-white border-brand-line focus-visible:ring-brand-green" data-testid="create-user-password" />
          </div>
          <div>
            <Label className="text-sm font-semibold text-brand-ink mb-1.5 inline-block">Role</Label>
            <Select value={form.role} onValueChange={(v) => set("role", v)}>
              <SelectTrigger className="bg-white border-brand-line" data-testid="create-user-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="client" data-testid="create-user-role-client">Client</SelectItem>
                <SelectItem value="admin" data-testid="create-user-role-admin">Administrator</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-brand-green hover:bg-brand-greenHover text-white" data-testid="create-user-submit">
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Create user
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
